import type { CSSProperties, ReactNode } from 'react'

import type { FriendMark } from '~/api/types'
import { whoIs } from '~/calendar/views'
import { lettersOf, usePhoto } from '~/social/Avatar'

import { AppLink } from './AppLink'

import s from './Faces.module.css'

/**
 * The friends on a gig, as a stack of round faces: going first and lit, saved behind and
 * dimmed, `+N` past the cap.
 *
 * A drawing of what `whoIs` already says, and nothing more. The going/saved split, the
 * tense and the "and N more" form are that function's — the stack's `title` and its
 * accessible name *are* its sentence, so a change to a word there changes the hover here
 * without this file knowing. The order the faces are drawn in is the order `whoIs` names
 * them: every going mark in the order they arrived, then every saved one.
 *
 * One component for every surface. The surface says how big a face is and where the stack
 * goes when pressed; the stack does not know whether it is on a card, a row or a chip. The
 * one place that is not a stack is the sheet, where each face is its own link to a person
 * rather than one link to the gig: it draws `Face` directly, and nothing else does.
 *
 * The photograph-or-initials rule, the address and the grade are `Avatar`'s, imported. An
 * initials plate is a `<span>` and renders no `<img>` at all — the flag on the mark is what
 * decides, so a page of faces costs one request per friend with a picture and none for the
 * rest. Every `<img>` is `lazy` and `async` like the artwork beside it.
 */

/** How many faces before the rest become a number. */
export const FACES_SHOWN = 3

export interface FacesProps {
  friends: readonly FriendMark[]
  /** The side of one face, in CSS pixels. Read by the stylesheet as `--size`. */
  size: number
  /** Faces before the `+N` plate. The month chip asks for one. */
  max?: number
  /** A night that has been: the sentence says "went" and the rings go grey. */
  past?: boolean
  /**
   * Where the stack goes: the gig's own sheet, `/e/<id>`. Every surface that carries a
   * stack already opens the gig, and the sheet puts who is going at the top — so on a touch
   * screen this is the target, on a pointer the element whose `title` is the sentence, and
   * on a keyboard the one stop on a card that reads who is going.
   */
  to: string
  /**
   * Draw the going and nobody else. The sentence still names everybody — it is the answer
   * to "who", and a save is part of it — but a month cell has room for one fact per gig,
   * and the fact is who will be there. `+N` then counts the going not drawn, not the saved.
   */
  only?: 'going'
  /**
   * Whether the undrawn are counted on a `+N` plate. Off where words sit beside the faces
   * and already end "and N more": the same number twice, once as a pill and once as a
   * sentence, is the compact row's "1 going" beside one face over again.
   */
  plate?: boolean
  /**
   * Faces down rather than across, overlapping the same way. For a column with room for one
   * face's width and a chip's height — the month's — where a stack across would cover the
   * chip, and a column widened for it would move every mark.
   */
  column?: boolean
  /**
   * Words after the faces, inside the link — the line form's sentence. Inside rather than
   * beside, so the whole line is one target and one tab stop rather than a link with a
   * dead sentence hanging off it.
   */
  children?: ReactNode
  className?: string
}

export function Faces({
  friends,
  size,
  max = FACES_SHOWN,
  past = false,
  to,
  only,
  plate = true,
  column = false,
  children,
  className,
}: FacesProps) {
  if (friends.length === 0) return null
  // A stable partition, not a sort: `whoIs` names the going in the order they arrived and
  // then the saved in theirs, and the faces have to read in the same order as the sentence
  // under the pointer.
  const going = friends.filter(f => f.going)
  const drawable = only === 'going' ? going : [...going, ...friends.filter(f => !f.going)]
  const shown = drawable.slice(0, max)
  const hidden = drawable.length - shown.length
  const sentence = whoIs(friends, { past })
  if (shown.length === 0) return null
  return (
    <AppLink
      to={to}
      className={className ? `${s.stack} ${className}` : s.stack}
      title={sentence}
      aria-label={sentence}
      data-size={size}
      {...(past ? { 'data-past': 'true' as const } : {})}
      {...(column ? { 'data-column': 'true' as const } : {})}
      style={{ '--size': `${size}px` } as CSSProperties}
    >
      {shown.map((friend, i) => (
        // Across, earlier faces paint over later ones, so the first going face is the whole
        // one and each face behind it shows its left edge; flex paints later siblings on top,
        // which is the other way round, hence the descending z-index. Down a column it is the
        // other way again: later on top, like shingles, so every face shows its top half and
        // the plate under the last one shows whole. With first-on-top, a middle face was a
        // sliver -- the face above took its top and the plate below took its bottom.
        <Face
          key={friend.id}
          friend={friend}
          going={friend.going}
          order={column ? i + 1 : shown.length - i}
        />
      ))}
      {plate && hidden > 0 ? <span className={s.more}>+{hidden}</span> : null}
      {children !== undefined && children !== null ? (
        <span className={s.words}>{children}</span>
      ) : null}
    </AppLink>
  )
}

export interface FaceProps {
  friend: Pick<FriendMark, 'id' | 'name' | 'hasAvatar'>
  /**
   * Lit with the going ring, or stepped back as saved. Left out, the face is neither — the
   * sheet draws its two rows under headings that say which, and a dimmed face under
   * "saved this" would be saying it twice.
   */
  going?: boolean
  /** Paint order in a stack; see the caller. */
  order?: number
}

/**
 * One friend, round. On its own for the sheet, where every face is its own link to a
 * profile and a stack — one link, one sentence — is the wrong shape. Sized by `--size` on
 * whatever contains it, which the stack sets inline and the sheet's row sets in its own
 * stylesheet.
 */
export function Face({ friend, going, order }: FaceProps) {
  const { src, onError } = usePhoto(friend.id, friend.hasAvatar)
  return (
    <span
      className={s.face}
      {...(going === undefined ? {} : { 'data-going': going ? 'true' : 'false' })}
      style={order === undefined ? undefined : { zIndex: order }}
    >
      {src === null ? (
        <span className={s.plate} role="img" aria-label={friend.name}>
          {lettersOf(friend.name) || '·'}
        </span>
      ) : (
        <img
          className={s.photo}
          src={src}
          alt={friend.name}
          loading="lazy"
          decoding="async"
          onError={onError}
        />
      )}
    </span>
  )
}
